import { useState } from 'react';
import { Header } from '../components/dashboard/DashboardHeader';
import { DashboardChart } from '../components/dashboard/DashboardChart';
import { InfoPanel } from '../components/dashboard/InfoPanel';
import { MetricToggle } from '../components/dashboard/MetricToggle';
import { OperationalMetrics } from '../components/dashboard/OperationalMetrics';
import { CertificatesSection } from '../components/dashboard/CertificatesSection';
import { IndustryBenchmark } from '../components/dashboard/IndustryBenchmark';
import { CEUniversity } from '../components/dashboard/CEUniversity';
import { CertificatePreview } from '../components/dashboard/CertificatePreview';
import certificateImage from '../assets/images/company.jpeg';

export type MetricType = 'CO2e' | 'Waste' | 'TreesSaved';

export interface DataPoint {
  id: string;
  name: string;
  co2e: number;
  waste: number;
  treesSaved: number;
  description: string;
  potentialCo2e?: number;
  potentialWaste?: number;
  potentialTreesSaved?: number;
}

const initialData: DataPoint[] = [
  {
    id: 'chep',
    name: 'Total CHEP Pallets',
    co2e: -1340,
    waste: -2412,  // -1340 × 1.8
    treesSaved: 67,
    description: 'Emissions reduced through optimized pallet reuse and circular logistics'
  },
  {
    id: 'supplier',
    name: 'Supplier Integration',
    co2e: -828,
    waste: -2484,  // -828 × 3.0
    treesSaved: 41,
    description: 'Carbon savings from collaborative supplier network optimization'
  },
  {
    id: 'cycle',
    name: 'Cycle Time',
    co2e: -233,
    waste: -186.4,  // -233 × 0.8
    treesSaved: 12,
    description: 'Efficiency gains from reduced production cycle times'
  },
  {
    id: 'transport',
    name: 'Transport Collaboration',
    co2e: -1,
    waste: -1.2,  // -1 × 1.2
    treesSaved: 0.05,
    description: 'Marginal improvements from shared transportation initiatives'
  }
];

export default function App() {
  const [data, setData] = useState<DataPoint[]>(initialData);
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('CO2e');
  const [operationalImpacts, setOperationalImpacts] = useState<{ [key: string]: number }>({});
  const [selectedSupplier, setSelectedSupplier] = useState('All');
  const [isCEUniversityOpen, setIsCEUniversityOpen] = useState(false);

  const updateDataPoint = (id: string, value: number) => {
    setData(prevData =>
      prevData.map(item =>
        item.id === id
          ? {
            ...item,
            co2e: selectedMetric === 'CO2e' ? value : item.co2e,
            waste: selectedMetric === 'Waste' ? value : item.waste,
            treesSaved: selectedMetric === 'TreesSaved' ? value : item.treesSaved
          }
          : item
      )
    );
  };

  const handleOperationalImpactChange = (impacts: { [key: string]: number }) => {
    setOperationalImpacts(impacts);

    // Define initiative-specific waste-to-CO₂ conversion ratios
    const wasteRatios: { [key: string]: number } = {
      'chep': 1.8,        // Reusable Pallets: 1.8 kg waste per ton CO₂
      'transport': 1.2,   // Transport Collaboration: 1.2 kg waste per ton CO₂
      'supplier': 3.0,    // Supplier Integration: 3.0 kg waste per ton CO₂
      'cycle': 0.8        // Cycle Time Reduction: 0.8 kg waste per ton CO₂
    };

    // Update the data based on operational metrics while preserving structure
    setData(prevData =>
      prevData.map((item, index) => {
        const impact = impacts[item.id];
        if (impact !== undefined) {
          // Get initiative-specific waste ratio, fallback to 2 for unknown initiatives
          const wasteRatio = wasteRatios[item.id] || 2;
          const treesRatio = 0.05; // 1 tCO2e = 0.05 trees

          return {
            ...item,
            co2e: impact,
            waste: impact * wasteRatio,
            // Negative CO₂e (reduction) = positive trees saved
            // Positive CO₂e (increase) = negative trees saved (trees lost)
            treesSaved: -impact * treesRatio
          };
        }
        // If no impact defined, keep original data
        return initialData[index];
      })
    );
  };

  const resetData = () => {
    setData(initialData);
  };

  const getMetricValue = (item: DataPoint) => {
    switch (selectedMetric) {
      case 'CO2e':
        return item.co2e;
      case 'Waste':
        return item.waste;
      case 'TreesSaved':
        return item.treesSaved;
      default:
        return item.co2e;
    }
  };

  const netImpact = data.reduce((sum, item) => sum + getMetricValue(item), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />

      <div className="max-w-[1600px] mx-auto p-8">
        {/* Controls */}
        <div className="flex justify-between items-center mb-6">
          <MetricToggle
            selectedMetric={selectedMetric}
            onMetricChange={setSelectedMetric}
          />
          <button
            onClick={resetData}
            className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:shadow-md transition-all duration-200 shadow-sm"
          >
            Reset Data
          </button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart Section */}
          <div className="lg:col-span-2">
            <DashboardChart
              data={data}
              selectedMetric={selectedMetric}
              onUpdateDataPoint={updateDataPoint}
              netImpact={netImpact}
              selectedSupplier={selectedSupplier}
            />

            {/* Certificates Section - Below Chart */}
            <div className="mt-6">
              <CertificatesSection
                selectedSupplier={selectedSupplier}
                onSupplierChange={setSelectedSupplier}
              />

              {/* Certificate Preview - Below Certificates Section */}
              <div className="mt-6">
                <CertificatePreview certificateImage={certificateImage} />
              </div>
            </div>
          </div>

          {/* Info Panel */}
          <div className="lg:col-span-1">
            <InfoPanel
              netImpact={netImpact}
              selectedMetric={selectedMetric}
              data={data}
              onOpenCEUniversity={() => setIsCEUniversityOpen(true)}
            />
          </div>
        </div>

        {/* Operational Metrics Section */}
        <div className="mt-8">
          <OperationalMetrics onImpactChange={handleOperationalImpactChange} />
        </div>

        {/* Industry Benchmark Section */}
        <div className="mt-8">
          <IndustryBenchmark
            customerCategory="Consumer Fresh Foods"
            industryAverageCycleTime={45}
            customerCycleTime={28}
          />
        </div>
      </div>

      {/* CE University Dialog */}
      <CEUniversity
        isOpen={isCEUniversityOpen}
        onClose={() => setIsCEUniversityOpen(false)}
      />
    </div>
  );
}
